import {
  ArrowRight,
  AudioLines,
  Box,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Globe,
  Keyboard,
  Languages,
  LayoutGrid,
  Layers,
  ListVideo,
  Network,
  PackageCheck,
  Paintbrush,
  Palette,
  Server,
  ServerCog,
  ShieldCheck,
  Zap,
} from "lucide-react";
import nativeAudioImg from "@/assets/iptv-app/native-audio.jpg";
import nativeCategoriesImg from "@/assets/iptv-app/native-categories.jpg";
import nativeEpgImg from "@/assets/iptv-app/native-epg.jpg";
import nativePlayerImg from "@/assets/iptv-app/native-player.jpg";
import nativeSetupImg from "@/assets/iptv-app/native-setup.png";
import stalkerChannelsImg from "@/assets/iptv-app/stalker-channels.jpg";
import stalkerHomeImg from "@/assets/iptv-app/stalker-home.jpg";
import stalkerPlayerMenuImg from "@/assets/iptv-app/stalker-player-menu.jpg";
import stalkerSettingsImg from "@/assets/iptv-app/stalker-settings.jpg";
import tvipChannelsImg from "@/assets/iptv-app/tvip-channels.jpg";
import tvipEpgImg from "@/assets/iptv-app/tvip-epg.jpg";
import tvipHomeImg from "@/assets/iptv-app/tvip-home.jpg";
import { RevealSection, revealClass, useReveal } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function IptvAppPage() {
  const { messages, site, href } = useSite();
  const home = localizeUrl("/", site.locale, href);
  const demoCta = localizeUrl("/contacts?inquiry=demo#inquiry-form", site.locale, href);
  const salesCta = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);
  const stbLineup = localizeUrl("/#lineup", site.locale, href);
  const alcatraz = localizeUrl("/solutions/alcatraz-dms", site.locale, href);

  const heroStats = [
    { icon: Layers, label: messages.iptvAppHeroStat1 },
    { icon: Palette, label: messages.iptvAppHeroStat2 },
    { icon: Zap, label: messages.iptvAppHeroStat3 },
  ];

  const pillars = [
    { icon: CalendarDays, title: messages.iptvAppPillar1Title, body: messages.iptvAppPillar1Body },
    { icon: LayoutGrid, title: messages.iptvAppPillar2Title, body: messages.iptvAppPillar2Body },
    { icon: AudioLines, title: messages.iptvAppPillar3Title, body: messages.iptvAppPillar3Body },
    { icon: Network, title: messages.iptvAppPillar4Title, body: messages.iptvAppPillar4Body },
    { icon: Keyboard, title: messages.iptvAppPillar5Title, body: messages.iptvAppPillar5Body },
    { icon: Languages, title: messages.iptvAppPillar6Title, body: messages.iptvAppPillar6Body },
  ];

  const galleryShots = [
    { image: nativeAudioImg, alt: messages.iptvAppAltNativeAudio },
    { image: stalkerPlayerMenuImg, alt: messages.iptvAppAltStalkerPlayerMenu },
    { image: tvipChannelsImg, alt: messages.iptvAppAltTvipChannels },
  ];

  const steps = [
    {
      icon: ClipboardList,
      title: messages.iptvAppProcessStep1Title,
      body: messages.iptvAppProcessStep1Body,
    },
    {
      icon: Paintbrush,
      title: messages.iptvAppProcessStep2Title,
      body: messages.iptvAppProcessStep2Body,
    },
    {
      icon: PackageCheck,
      title: messages.iptvAppProcessStep3Title,
      body: messages.iptvAppProcessStep3Body,
    },
  ];

  const faqs = [
    { q: messages.iptvAppFaqQ1, a: messages.iptvAppFaqA1 },
    { q: messages.iptvAppFaqQ2, a: messages.iptvAppFaqA2 },
    { q: messages.iptvAppFaqQ3, a: messages.iptvAppFaqA3 },
    { q: messages.iptvAppFaqQ4, a: messages.iptvAppFaqA4 },
  ];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={home} className="hover:text-foreground">
            {messages.iptvAppBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={localizeUrl("/solutions", site.locale, href)} className="hover:text-foreground">
            {messages.iptvAppBreadcrumbSolutions}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.iptvAppBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-50"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[480px] w-[640px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:py-24">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              {messages.iptvAppHeroEyebrow}
            </div>
            <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl lg:text-5xl">
              {messages.iptvAppHeroTitle}{" "}
              <span className="text-gradient">{messages.iptvAppHeroTitleAccent}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">{messages.iptvAppHeroSub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="animate-glow">
                <a href={demoCta}>{messages.iptvAppHeroCtaDemo}</a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href={salesCta}>{messages.iptvAppHeroCtaSales}</a>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {heroStats.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
                >
                  <Icon className="h-3.5 w-3.5 text-primary" />
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="animate-fade-up">
            <TvScreenshotFrame
              image={nativePlayerImg}
              alt={messages.iptvAppAltNativePlayer}
              eager
            />
          </div>
        </div>
      </section>

      {/* Backend modes — intro */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.iptvAppModesEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.iptvAppModesTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              {messages.iptvAppModesDescription}
            </p>
          </div>
        </div>
      </RevealSection>

      {/* Mode 1 — M3U + XMLTV */}
      <ModeSection
        index={1}
        icon={ListVideo}
        layout="imageRight"
        title={messages.iptvAppMode1Title}
        body={messages.iptvAppMode1Body}
        bullets={[
          messages.iptvAppMode1Bullet1,
          messages.iptvAppMode1Bullet2,
          messages.iptvAppMode1Bullet3,
          messages.iptvAppMode1Bullet4,
        ]}
        primaryImage={nativeEpgImg}
        primaryAlt={messages.iptvAppAltNativeEpg}
        secondaryImage={nativeCategoriesImg}
        secondaryAlt={messages.iptvAppAltNativeCategories}
      />

      {/* Mode 2 — Stalker / Ministra */}
      <ModeSection
        index={2}
        icon={Server}
        layout="imageLeft"
        muted
        title={messages.iptvAppMode2Title}
        body={messages.iptvAppMode2Body}
        bullets={[
          messages.iptvAppMode2Bullet1,
          messages.iptvAppMode2Bullet2,
          messages.iptvAppMode2Bullet3,
          messages.iptvAppMode2Bullet4,
        ]}
        primaryImage={stalkerHomeImg}
        primaryAlt={messages.iptvAppAltStalkerHome}
        secondaryImage={stalkerChannelsImg}
        secondaryAlt={messages.iptvAppAltStalkerChannels}
      />

      {/* Mode 3 — TVIP / Hibox / Aura */}
      <ModeSection
        index={3}
        icon={Globe}
        layout="imageRight"
        title={messages.iptvAppMode3Title}
        body={messages.iptvAppMode3Body}
        bullets={[
          messages.iptvAppMode3Bullet1,
          messages.iptvAppMode3Bullet2,
          messages.iptvAppMode3Bullet3,
          messages.iptvAppMode3Bullet4,
        ]}
        primaryImage={tvipHomeImg}
        primaryAlt={messages.iptvAppAltTvipHome}
        secondaryImage={tvipEpgImg}
        secondaryAlt={messages.iptvAppAltTvipEpg}
      />

      {/* White-label & prepackaging */}
      <ModeSection
        icon={Palette}
        layout="imageLeft"
        muted
        eyebrow={messages.iptvAppWhiteLabelEyebrow}
        title={messages.iptvAppWhiteLabelTitle}
        body={messages.iptvAppWhiteLabelBody}
        bullets={[
          messages.iptvAppWhiteLabelBullet1,
          messages.iptvAppWhiteLabelBullet2,
          messages.iptvAppWhiteLabelBullet3,
          messages.iptvAppWhiteLabelBullet4,
        ]}
        primaryImage={nativeSetupImg}
        primaryAlt={messages.iptvAppAltNativeSetup}
        secondaryImage={stalkerSettingsImg}
        secondaryAlt={messages.iptvAppAltStalkerSettings}
      />

      {/* Feature pillars */}
      <RevealSection className="border-b border-border/60 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.iptvAppPillarsEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.iptvAppPillarsTitle}
            </h2>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pillars.map(({ icon: Icon, title, body }) => (
              <li
                key={title}
                className="rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur transition-colors hover:border-primary/30"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-sm font-semibold text-foreground">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ul>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {galleryShots.map(({ image, alt }) => (
              <figure
                key={alt}
                className="rounded-2xl border border-border/60 bg-card/80 p-1.5 shadow-lg shadow-primary/5 backdrop-blur"
              >
                <img
                  src={image}
                  alt={alt}
                  loading="lazy"
                  className="block aspect-video w-full rounded-xl object-cover"
                />
                <figcaption className="sr-only">{alt}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* Content security — certified Verimatrix partner (one-liner) */}
      <RevealSection className="border-b border-border/60 bg-card/40 py-8">
        <div className="mx-auto flex max-w-4xl items-start gap-3 px-6 text-sm text-muted-foreground sm:items-center">
          <ShieldCheck aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-primary sm:mt-0" />
          <p className="leading-relaxed">{messages.verimatrixNote}</p>
        </div>
      </RevealSection>

      {/* How it works */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.iptvAppProcessEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.iptvAppProcessTitle}
            </h2>
          </div>

          <ol className="relative mt-12 grid grid-cols-1 gap-10 sm:grid-cols-3">
            <span
              aria-hidden
              className="pointer-events-none absolute left-0 right-0 top-5 hidden h-px bg-primary/30 sm:block"
            />
            {steps.map(({ icon: Icon, title, body }, i) => (
              <li key={title} className="relative flex flex-col">
                <span className="relative z-10 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md ring-4 ring-muted/20">
                  <Icon className="h-5 w-5" />
                </span>
                <p className="mt-4 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  0{i + 1}
                </p>
                <h3 className="mt-1 text-base font-semibold text-foreground">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </RevealSection>

      {/* Ecosystem band */}
      <RevealSection className="border-b border-border/60 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-primary/10 p-8 sm:p-12">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-px -z-10 rounded-3xl bg-gradient-to-tr from-primary/30 via-transparent to-primary/10 opacity-70 blur-2xl"
            />
            <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-background/60 px-3 py-1 text-xs text-foreground backdrop-blur">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                  {messages.iptvAppEcosystemEyebrow}
                </div>
                <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl">
                  {messages.iptvAppEcosystemTitle}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {messages.iptvAppEcosystemBody}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  variant="secondary"
                  className="h-11 rounded-lg px-6 font-medium"
                >
                  <a href={stbLineup}>
                    <Box className="mr-1 h-4 w-4" />
                    {messages.iptvAppEcosystemLinkStb}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-11 rounded-lg px-6 font-medium"
                >
                  <a href={alcatraz}>
                    <ServerCog className="mr-1 h-4 w-4" />
                    {messages.iptvAppEcosystemLinkAlcatraz}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.iptvAppFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.iptvAppFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-20 text-background">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.iptvAppFinalEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.iptvAppFinalTitle}{" "}
            <span className="text-gradient">{messages.iptvAppFinalTitleAccent}</span>
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.iptvAppFinalBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary">
              <a href={demoCta}>{messages.iptvAppFinalCta}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background"
            >
              <a href={salesCta}>{messages.iptvAppFinalCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}

/** TV-style frame for the hero screenshot (mirrors the Alcatraz push-band frame). */
function TvScreenshotFrame({ image, alt, eager }: { image: string; alt: string; eager?: boolean }) {
  return (
    <figure className="relative mx-auto max-w-[720px]">
      <div aria-hidden className="absolute -inset-6 -z-10 rounded-full bg-primary/15 blur-3xl" />
      <div className="relative rounded-[1.25rem] border border-foreground/20 bg-[#06080d] p-2 shadow-2xl shadow-primary/10">
        <div className="overflow-hidden rounded-[0.75rem] border border-white/10 bg-black">
          <img
            src={image}
            alt={alt}
            loading={eager ? "eager" : "lazy"}
            fetchPriority={eager ? "high" : "auto"}
            className="block aspect-video w-full object-cover"
          />
        </div>
        <div
          aria-hidden
          className="absolute bottom-1 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-white/15"
        />
      </div>
      <div aria-hidden className="mx-auto -mt-0.5 flex w-[64%] justify-between px-5">
        <div className="h-6 w-10 origin-top-left -skew-x-[20deg] rounded-b bg-gradient-to-b from-[#171d28] to-[#070a10] shadow-md" />
        <div className="h-6 w-10 origin-top-right skew-x-[20deg] rounded-b bg-gradient-to-b from-[#171d28] to-[#070a10] shadow-md" />
      </div>
      <div
        aria-hidden
        className="mx-auto -mt-2 h-3 w-[76%] rounded-full bg-foreground/10 blur-sm"
      />
      <figcaption className="sr-only">{alt}</figcaption>
    </figure>
  );
}

/** Primary screenshot with a smaller second shot overlapping the corner. */
function ScreenshotDuo({
  primaryImage,
  primaryAlt,
  secondaryImage,
  secondaryAlt,
  overlapLeft,
}: {
  primaryImage: string;
  primaryAlt: string;
  secondaryImage: string;
  secondaryAlt: string;
  overlapLeft?: boolean;
}) {
  return (
    <div className="relative sm:pb-12">
      <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
      <figure className="rounded-2xl border border-border/60 bg-card/80 p-2 shadow-xl shadow-primary/10 backdrop-blur">
        <img
          src={primaryImage}
          alt={primaryAlt}
          loading="lazy"
          className="block aspect-video w-full rounded-xl object-cover"
        />
        <figcaption className="sr-only">{primaryAlt}</figcaption>
      </figure>
      <figure className="mt-4 rounded-2xl border border-border/60 bg-card/90 p-1.5 shadow-2xl shadow-primary/15 backdrop-blur sm:absolute sm:bottom-0 sm:mt-0 sm:w-[52%] sm:-right-4 lg:-right-6">
        {overlapLeft ? <span className="sr-only">{secondaryAlt}</span> : null}
        <img
          src={secondaryImage}
          alt={secondaryAlt}
          loading="lazy"
          className="block aspect-video w-full rounded-xl object-cover"
        />
        <figcaption className="sr-only">{secondaryAlt}</figcaption>
      </figure>
    </div>
  );
}

/** Alternating text + screenshot-duo section, used for backend modes and white-label. */
function ModeSection({
  index,
  icon: Icon,
  eyebrow,
  title,
  body,
  bullets,
  primaryImage,
  primaryAlt,
  secondaryImage,
  secondaryAlt,
  layout,
  muted,
}: {
  index?: number;
  icon: typeof Server;
  eyebrow?: string;
  title: string;
  body: string;
  bullets: string[];
  primaryImage: string;
  primaryAlt: string;
  secondaryImage: string;
  secondaryAlt: string;
  layout: "imageLeft" | "imageRight";
  muted?: boolean;
}) {
  const imageLeft = layout === "imageLeft";
  const { ref, shown } = useReveal<HTMLElement>();
  return (
    <section
      ref={ref}
      className={`${revealClass(shown)} border-b border-border/60 py-20 ${
        muted ? "bg-muted/20" : "bg-background"
      }`}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
        <div className={imageLeft ? "order-2 lg:order-1" : "order-2"}>
          <ScreenshotDuo
            primaryImage={primaryImage}
            primaryAlt={primaryAlt}
            secondaryImage={secondaryImage}
            secondaryAlt={secondaryAlt}
            overlapLeft={imageLeft}
          />
        </div>
        <div className={imageLeft ? "order-1 lg:order-2" : "order-1"}>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </span>
            {typeof index === "number" ? (
              <span className="text-xs font-medium uppercase tracking-wider text-primary">
                0{index}
              </span>
            ) : eyebrow ? (
              <span className="text-xs font-medium uppercase tracking-wider text-primary">
                {eyebrow}
              </span>
            ) : null}
          </div>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">{body}</p>
          <ul className="mt-6 space-y-2 text-sm text-foreground/90">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
